import { defineStore, acceptHMRUpdate } from 'pinia'
import baseAxios from '@/axios'

const TOKEN_KEY = 'jwt-token'
const REFRESH_KEY = 'jwt-refresh-token'
const EXPIRES_KEY = 'jwt-expires'
const USER_KEY = 'user'

export const useAuthStore = defineStore({
  id: 'auth',
  state: () => ({
    token: localStorage.getItem(TOKEN_KEY),
    refreshToken: localStorage.getItem(REFRESH_KEY),
    expiresDate: localStorage.getItem(EXPIRES_KEY),
    user: JSON.parse(localStorage.getItem(USER_KEY)) ?? {},
  }),
  actions: {
    async login(payload) {
      const { data } = await baseAxios.post('/oauth/token', payload)
      this.token = data.access_token
      this.refreshToken = data.refresh_token
      this.expiresDate = data.expires_in + new Date().getTime() + 100000000
      this.user = data.user

      localStorage.setItem(TOKEN_KEY, this.token)
      localStorage.setItem(REFRESH_KEY, this.refreshToken)
      localStorage.setItem(EXPIRES_KEY, this.expiresDate)
      localStorage.setItem(USER_KEY, JSON.stringify(this.user))
    },

    async refresh() {
      const { data } = await baseAxios.post(
        `/oauth/token?grant_type=refresh_token&client_id=${
          import.meta.env.VITE_CLIENT_ID
        }&client_secret=${import.meta.env.VITE_CLIENT_SECRET}&refresh_token=${
          this.refreshToken
        }`
      )
      this.token = data.access_token
      this.refreshToken = data.refresh_token
      this.expiresDate = data.expires_in + new Date().getTime() + 100000000
      this.user = data.user

      localStorage.setItem(TOKEN_KEY, this.token)
      localStorage.setItem(REFRESH_KEY, this.refreshToken)
      localStorage.setItem(EXPIRES_KEY, this.expiresDate)
      localStorage.setItem(USER_KEY, JSON.stringify(this.user))
    },

    async forgotPassword(payload) {
      const data = await baseAxios.post('/users/password', payload)

      return data
    },

    async invitationUser(payload) {
      await baseAxios.put('users/invitation', payload)
    },

    async resetPassword(payload) {
      return await baseAxios.put('/users/password', payload)
    },

    async changePassword(id, payload) {
      return await baseAxios.post(`/users/${id}/update_password`, payload)
    },

    async logout() {
      await baseAxios.post(
        `/oauth/revoke?client_id=${
          import.meta.env.VITE_CLIENT_ID
        }&client_secret=${import.meta.env.VITE_CLIENT_SECRET}`
      )

      this.token = null
      this.refreshToken = null
      this.expiresDate = null
      this.user = {}
      localStorage.removeItem(TOKEN_KEY)
      localStorage.removeItem(REFRESH_KEY)
      localStorage.removeItem(EXPIRES_KEY)
      localStorage.removeItem(USER_KEY)
    },
  },
  getters: {
    isExpired: (state) => new Date().getTime() >= state.expiresDate,
    isAuthenticated: (state) =>
      !!state.token && !(new Date().getTime() >= state.expiresDate),
    isAdmin: (state) => state.user.role === 'admin',
    isAreaManager: (state) => state.user.role === 'area_manager',
    isCourier: (state) => state.user.role === 'courier',
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot))
}
